import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

// IconButton — story 2-147c (P10 §1.3's action-hierarchy rule; P10 §2).
//
// ── THE GAP ──────────────────────────────────────────────────────────────
// Two populations of icon-only control exist in the tree. `Button` used with
// its own `iconOnly` prop is already internally consistent — every call site
// relies on that primitive's own sr-only-children contract for the
// accessible name — and is UNAFFECTED by this file. The real gap is the
// second population: 11 raw `<button>` elements across 9 files
// (`FilterBar`, `DateInput`, `Chip`, `TopBar`, `Sidebar` ×4, `AgentPanel`,
// `ThemeToggle`, `PaywallModal`) with no shared contract at all — three
// different accessible-name mechanisms coexist, and `PaywallModal.tsx`'s
// close button shipped with NONE (a real, confirmed a11y bug closed by this
// component's required `aria-label`, not by convention).
//
// ── Accessible name is REQUIRED, never optional ─────────────────────────
// An icon-only control has no visible text, so `aria-label` is the only name
// it will ever have. Typing it as required (not `aria-label?`) closes
// `PaywallModal`'s gap by construction — a call site literally cannot
// compile one of these without a name.
//
// ── Why `ghost`/`destructive` carry no baked color, hover or border ────────
// Every migrated call site already had its own bespoke chrome (a border here,
// a specific muted shade there, a different hover target) developed
// independently over several stories. Baking a default hover/color into
// `ghost` risks silently fighting a call site's own override: two
// same-property Tailwind utility classes both present on one element resolve
// by the utilities' position in the COMPILED stylesheet, not by source order
// in `className` — a losing override would look right in the diff and wrong
// in the browser. Leaving `ghost`/`destructive` unopinionated on color means
// every call site's existing look transfers byte-for-byte via its own
// `className`, and the shared, load-bearing part — focus ring, hit-target
// sizing, disabled handling, the required name — comes from here instead.
// `primary`/`secondary` are the one exception: they reuse `.btn-glass`'s own
// self-contained chrome, the same class `Button.tsx` itself composes.

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive';
type Size = 'sm' | 'md';
type Shape = 'square' | 'circle';

const VARIANT_CLASS: Record<Variant, string> = {
  primary: 'btn-glass btn-primary',
  secondary: 'btn-glass btn-secondary',
  ghost: '',
  // The one variant given a sensible default: no migrated call site needs
  // it today (a real irreversible-consequence icon-only action doesn't
  // exist in the 11 — see the story's own note that Sidebar's sign-out
  // stays `ghost`, it's chrome not a destructive-consequence button), so
  // there is nothing existing for a baked-in default to fight.
  destructive: 'text-destructive',
};

// `md` sits on the shared control-height scale (story 2-147qa, tokens.css
// `--control-h-md`) via the SAME `h-control`/`w-control` utilities
// Button/Input/Select/SegmentedControl now all consume — one boxed control
// height, not a per-component number. `sm` is a DELIBERATE exception, not
// competing debt: it is the compact, un-boxed footprint the inline
// clear/remove glyphs (FilterBar, DateInput, Chip) already used (`p-0.5`)
// — these glyphs sit INSIDE a smaller host control (a chip, a compact
// input), so forcing them onto `--control-h-sm`'s own 28px box would make
// the clear button bigger than the thing it lives in. Judgment call
// recorded in SESSION-HANDOFF.md: `sm` stays padding-based on purpose,
// `md` is the one that had to converge.
const SIZE_CLASS: Record<Size, string> = {
  sm: 'p-0.5',
  md: 'h-control w-control',
};

const SHAPE_CLASS: Record<Shape, string> = {
  square: 'rounded-lg',
  circle: 'rounded-full',
};

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Decorative — hidden from the accessibility tree. The accessible name always comes from `aria-label` below, never from this. */
  icon: ReactNode;
  /** REQUIRED accessible name — never optional (see header). */
  'aria-label': string;
  variant?: Variant;
  size?: Size;
  shape?: Shape;
  /** Toggle-button state — sets `aria-pressed`. Omit for non-toggle buttons. */
  pressed?: boolean | undefined;
  /**
   * Story 2-147qa — same loading contract as `Button`: shows a spinner in
   * place of the icon, sets `aria-busy`, and disables the control. Icon-only
   * controls have no separate text slot to show a "Saving…" label in, so
   * the spinner replacing the icon IS the loading affordance.
   */
  loading?: boolean;
  /**
   * Optional VISIBLE content rendered after the icon (e.g. `ThemeToggle`'s
   * responsive "Light mode"/"Dark mode" label, hidden below `sm`). The
   * accessible name still comes only from `aria-label` — this is
   * supplementary visible chrome, not a second naming mechanism. Presence of
   * `children` suppresses the `size`/`shape` box classes (the control
   * becomes auto-width to fit its own content, same as `Button`).
   */
  children?: ReactNode;
  className?: string;
}

// Composes Button's own focus-ring/disabled/variant vocabulary rather than
// wrapping <Button iconOnly> directly: Button's iconOnly mechanism (sr-only
// children as the accessible name) is the OTHER, already-consistent
// population's contract — this primitive intentionally uses `aria-label`
// instead, the mechanism every one of the 11 raw-<button> call sites already
// used, so migrating them changes their shell, not their accessible-name
// mechanism.
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    icon,
    variant = 'ghost',
    size = 'md',
    shape = 'square',
    pressed,
    loading = false,
    className = '',
    type = 'button',
    disabled,
    children,
    ...rest
  },
  ref,
) {
  const boxClass = children ? '' : `${SIZE_CLASS[size]} ${SHAPE_CLASS[shape]}`;
  const isDisabled = disabled || loading;
  const displayIcon = loading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : icon;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-pressed={pressed}
      aria-busy={loading || undefined}
      className={`focus-ring inline-flex shrink-0 items-center justify-center disabled:pointer-events-none disabled:opacity-50 ${VARIANT_CLASS[variant]} ${boxClass} ${className}`}
      {...rest}
    >
      <span aria-hidden="true" className="inline-flex shrink-0 items-center justify-center">
        {displayIcon}
      </span>
      {children}
    </button>
  );
});
